import React from 'react';
import { connect } from 'react-redux';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { match } from 'react-router-dom';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, FormPickerDatas } from '../../../local-types';
// Modules
import { LightStructure, PrebookById, TParams } from '../../../redux/reducers/api-types';
import { selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import {
    actions as driverActions,
    selectors as driverSelectors,
    types as driverTypes,
    DriverById,
} from '../../../redux/reducers/drivers';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { actions as prebookActions, selectors as prebookSelectors } from '../../../redux/reducers/prebook';
// Ressources
import styles from './driver-edit-style';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';

interface DriverEditPageProps extends WithStyles, WithTranslation {
    match: match<TParams>;
    drivers: DriverById;
    controlTowers: LightStructure[];
    prebooks: PrebookById;
    getDriver: (id: string) => any;
    editDriver: (id: string, payload) => any;
    getControlTowers: () => any;
    getPrebookGroups: () => any;
    editDriverError: ErrorPayload;
    getControlTowersError: ErrorPayload;
    getDriverError: ErrorPayload;
    getPrebookGroupsError: ErrorPayload;
    isEditingDriver: boolean;
}

interface DriverEditPageState {
    formData: any;
}

class DriverEditPage extends React.Component<DriverEditPageProps, DriverEditPageState> {
    inputs: FormDatas[];

    canWritePrebookRead: boolean = UserUtils.checkRights({
        name: AccountRight.DRIVER_PREBOOK,
        mode: RightRank.R,
    });
    canWritePrebook: boolean = UserUtils.checkRights({
        name: AccountRight.DRIVER_PREBOOK,
        mode: RightRank.W,
    });

    constructor(props: DriverEditPageProps) {
        super(props);
        this.state = {
            formData: {},
        };
    }

    static getDerivedStateFromProps(props, state) {
        const id = props.match.params.id;
        const payload = props.drivers[id];
        if (payload) {
            return { formData: payload };
        }
        return null;
    }

    componentDidMount() {
        const { match, drivers, controlTowers, prebooks, getDriver, getControlTowers, getPrebookGroups } = this.props;

        const id = match.params.id;
        const driver = drivers[id];
        if (!driver) {
            getDriver(id);
        }
        if (controlTowers.length < 1) {
            getControlTowers();
        }
        if (prebooks && Object.keys(prebooks).length < 1) {
            getPrebookGroups();
        }
    }

    handleSubmit = (formData, event): void => {
        const { editDriver, match } = this.props;
        const id = match.params.id;

        if (formData.dispatch_groups) {
            const groups = {};
            formData.dispatch_groups = formData.dispatch_groups.map((e, index) => {
                return e.id;
            });
            formData.dispatch_groups.forEach((element) => {
                groups[element] = true;
            });
            formData.the_dispatch_groups = groups;
        }
        this.setState({ formData: formData });

        const payload = { ...formData };
        const filter = [
            'banned',
            'ban_reason',
            'ban_description',
            'banned_until_date',
            'nb_bookings',
            'nb_bookings_week_b2c',
            'nb_bookings_week_b2b',
            'nb_bookings_month_b2c',
            'nb_bookings_month_b2b',
            'total_net_price_without_vat_week_b2c',
            'total_net_price_without_vat_week_b2b',
            'total_net_price_without_vat_month_b2c',
            'total_net_price_without_vat_month_b2b',
            'history',
        ];
        filter.forEach((f) => {
            delete payload[f];
        });

        // The penalty is only managed from the driver view: sending its date back here would log a
        // spurious "penalty cancelled" entry in the driver history on every save.
        if (payload.details) {
            payload.details = { ...payload.details };
            delete payload.details.prebookSuspendedUntilDate;
        }

        editDriver(id, payload);
    };

    generatePrebookGroupsOptions = (): FormPickerDatas[] => {
        const { prebooks } = this.props;
        let options: FormPickerDatas[] = [];
        if (prebooks) {
            options = Object.values(prebooks).map((p) => {
                return { id: p.id, name: p.name };
            });
        }
        return options;
    };

    generateInputs = (): FormDatas[] => {
        const { t, controlTowers } = this.props;

        const inputs: FormDatas[] = [
            {
                title: t('common:form.general_infos'),
                datas: [
                    { id: 'lastname', label: t('common:form.lastname'), required: true },
                    {
                        id: 'firstname',
                        label: t('common:form.firstname'),
                        required: true,
                    },
                    {
                        id: 'email',
                        label: t('common:form.email'),
                        type: 'email',
                        required: true,
                    },
                    {
                        id: 'licence_number_atout_france',
                        label: t('vtc_number'),
                        required: true,
                    },
                    {
                        id: 'driving_licence_number',
                        label: t('licence_number'),
                        required: true,
                    },
                    {
                        id: 'phone_number',
                        label: t('common:form.phone_number'),
                        type: 'tel',
                        required: true,
                    },
                    { id: 'tpe', label: t('tpe'), type: 'boolean' },
                    {
                        id: 'details.controlTower.id',
                        label: t('control_tower'),
                        options: [...controlTowers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
                    },
                    this.canWritePrebookRead && {
                        id: 'details.prebookGroup.id',
                        label: t('prebook_group'),
                        disable: !this.canWritePrebook,
                        options: this.generatePrebookGroupsOptions(),
                    },
                    {
                        id: 'additional_info',
                        label: t('common:form.additional_infos'),
                    },
                ],
            },
        ];
        return inputs;
    };

    render() {
        const { formData } = this.state;
        const {
            getDriverError,
            editDriverError,
            getControlTowersError,
            getPrebookGroupsError,
            isEditingDriver,
            match,
        } = this.props;
        const id = match.params.id;

        return (
            <App needRights={{ name: AccountRight.PARTNER_SETTINGS, mode: RightRank.W }}>
                <ErrorMessage payload={getDriverError || editDriverError} />
                <ErrorMessage payload={getControlTowersError || getPrebookGroupsError} />
                {formData && formData.id && (
                    <EnhancedForm
                        payload={formData}
                        inputs={this.generateInputs()}
                        backRoute={`${paths.drivers}/${id}`}
                        onSubmit={this.handleSubmit}
                        loader={isEditingDriver}
                    />
                )}
            </App>
        );
    }
}

const DriverEditPageWithStyleAndTranslation = withStyles(styles)(withTranslation(['driver', 'common'])(DriverEditPage));

const mapStateToProps = (state) => ({
    drivers: driverSelectors.drivers(state),
    controlTowers: driverSelectors.controlTowers(state),
    prebooks: prebookSelectors.prebooks(state),
    getControlTowersError: errorsSelectors.getControlTowersError(state),
    getDriverError: errorsSelectors.getDriverError(state),
    getPrebookGroupsError: errorsSelectors.getPrebookGroupsError(state),
    editDriverError: errorsSelectors.editDriverError(state),
    isEditingDriver: pendingSelectors.isPending(state, driverTypes.EDIT_DRIVER),
});

const mapDispatchToProps = {
    getControlTowers: driverActions.getControlTowers,
    getDriver: driverActions.getDriver,
    editDriver: driverActions.editDriver,
    getPrebookGroups: prebookActions.getPrebooks,
};

export default connect(mapStateToProps, mapDispatchToProps)(DriverEditPageWithStyleAndTranslation);
